<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { useStore } from 'vuex'
import { useRoute } from 'vue-router'
import { API, HOST } from '@/utils/http'
import { LayerChart } from '@/components/layers'
import { Spinner } from '@/components/ui/spinner'
import { Clock, XCircle } from 'lucide-vue-next'
import { formatTime } from '@/utils/datetime'

const props = defineProps({
  layer: {
    type: Object,
    required: true
  }
})

const store = useStore()
const route = useRoute()
const orgId = computed(() => route.params.org_id)
const settings = computed(() => store.getters['user/getUserSettings'])

const results = ref([])
const activeResult = ref(null)
const isLoading = ref(true)
const error = ref(null)

const layerSubscription = computed(() => props.layer?.properties?.layer)

const statusToLabel = {
  '100': 'Pending Approval',
  '-100': 'Rejected',
  '-9': 'Preparing Data (Failed)',
  '-8': 'Cleaning Up (Failed)',
  '-7': 'Uploading Tiles (Failed)',
  '-6': 'Generating Tiles (Failed)',
  '-5': 'Merging Quads (Failed)',
  '-4': 'Uploading Data (Failed)',
  '-3': 'Delivering Data (Failed)',
  '-2': 'Ordering Data (Failed)',
  '-1': 'Starting (Failed)',
  '0': 'Done',
  '1': 'Starting',
  '2': 'Ordering Data',
  '3': 'Delivering Data',
  '4': 'Uploading Data',
  '5': 'Merging Quads',
  '6': 'Generating Tiles',
  '7': 'Uploading Tiles',
  '8': 'Cleaning Up',
  '9': 'Preparing Data',
  '10': 'Generating PNG'
}

const generateResourceLabel = (resourceId) => {
  if (!resourceId) return 'Unknown'
  const match = resourceId.match(/^(.+?)_([vV][\d.]+)_(\d+)$/)
  if (match) {
    const [, name, version, resolution] = match
    const labelName = name
      .replace(/_/g, ' ')
      .replace(/-/g, ' ')
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ')
    return `${labelName} ${resolution}m (${version})`
  }
  return resourceId
    .replace(/_/g, ' ')
    .replace(/-/g, ' ')
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ')
}

const resourceLabel = computed(() => {
  const resourceId = layerSubscription.value?.data?.parameters?.id
  return generateResourceLabel(resourceId)
})

const layerStatus = computed(() => {
  return layerSubscription.value?.status
})

const feature = computed(() => {
  if (!layerSubscription.value?.geometry) return null
  return {
    type: 'Feature',
    geometry: layerSubscription.value.geometry,
  }
})

const fetchResults = async () => {
  if (!layerSubscription.value) return

  isLoading.value = true
  error.value = null

  try {
    const response = await API({
      method: 'GET',
      url: `${HOST}/org/${orgId.value}/layers/${props.layer.uuid}/subscription/result`
    })
    results.value = response.data.sort((a, b) => new Date(a.item_datetime) - new Date(b.item_datetime))
    if (results.value.length > 0) {
      activeResult.value = results.value[0]
    }
  } catch (e) {
    console.error('Failed to fetch results:', e)
    error.value = 'Failed to load chart data'
  } finally {
    isLoading.value = false
  }
}

const handleActiveResultUpdate = (result) => {
  activeResult.value = result
  // Dispatch to store so map layer can react
  store.dispatch('layers/setActiveResult', {
    layerId: props.layer.uuid,
    result
  })
}

onMounted(() => {
  if (layerSubscription.value?.status === 0) {
    fetchResults()
  } else {
    isLoading.value = false
  }
})

watch(() => props.layer, () => {
  if (layerSubscription.value?.status === 0) {
    fetchResults()
  } else {
    // Reset stale data when switching to a non-done layer
    results.value = []
    activeResult.value = null
    isLoading.value = false
    error.value = null
  }
}, { deep: true })
</script>

<template>
  <div>
    <!-- Resource label and status -->
    <div class="mb-3">
      <p class="text-sm text-muted-foreground">{{ resourceLabel }}</p>
      <div v-if="layerStatus !== undefined && layerStatus !== null && parseInt(layerStatus) !== 0" class="flex items-center gap-2 mt-1">
        <Clock v-if="parseInt(layerStatus) === 100" class="text-amber-500 w-4 h-4" />
        <XCircle v-else-if="parseInt(layerStatus) < 0" class="text-red-400 w-4 h-4" />
        <Spinner v-else-if="parseInt(layerStatus) > 0" class="w-4 h-4" />
        <span class="text-sm">{{ statusToLabel[layerStatus] }}</span>
      </div>
      <p v-if="layerSubscription?.created_at" class="text-xs text-muted-foreground mt-1">
        {{ formatTime(layerSubscription.created_at, settings.tz, 'D MMMM YYYY HH:mm') }}
      </p>
    </div>

    <div v-if="isLoading" class="flex items-center gap-2 py-4">
      <Spinner class="w-4 h-4" />
      <span class="text-sm text-muted-foreground">Loading chart data...</span>
    </div>

    <div v-else-if="error" class="py-4">
      <p class="text-sm text-destructive">{{ error }}</p>
    </div>

    <div v-else-if="layerSubscription?.status > 0 || layerSubscription?.status === 100" class="py-4">
      <p class="text-sm text-muted-foreground">Layer is still processing</p>
    </div>

    <div v-else-if="layerSubscription?.status < 0" class="py-4">
      <p class="text-sm text-muted-foreground">Layer processing failed</p>
    </div>

    <div v-else-if="results.length === 0" class="py-4">
      <p class="text-sm text-muted-foreground">No data available</p>
    </div>

    <div v-else>
      <LayerChart
        :layer="layerSubscription"
        :feature="feature"
        :results="results"
        @update:activeResult="handleActiveResultUpdate"
      />
    </div>
  </div>
</template>
